import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "../components/SupabaseClient";
import { useAuth } from "../hooks/useAuth";

const ProfileContext = createContext(undefined);

export const ProfileProvider = ({ children }) => {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState(null);
  const location = useLocation();

  const fetchProfile = useCallback(async (userId) => {
    if (!userId) {
      setProfile(null);
      return;
    }

    setProfileLoading(true);
    setProfileError(null);

    try {
      const [profileResult, groupsResult] = await Promise.all([
        supabase.rpc("get_user_profile", { p_user_id: userId }),
        supabase.rpc("get_user_groups", { p_user_id: userId }),
      ]);

      const { data: profileData, error: profileError } = profileResult;
      const { data: groupsData, error: groupsError } = groupsResult;

      if (profileError) throw profileError;
      if (groupsError) throw groupsError;

      const combinedProfile = {
        ...(profileData || {}),
        groups: groupsData || [],
      };

      setProfile(combinedProfile);
    } catch (err) {
      console.error("Unexpected error fetching profile:", err);
      setProfileError(err.message || "Failed to fetch profile.");
      setProfile(null);
    } finally {
      setProfileLoading(false);
    }
  }, []);

  // Effect to fetch profile when user logs in, out, or navigates to a new page.
  useEffect(() => {
    if (user?.id) {
      fetchProfile(user.id);
    } else {
      setProfile(null);
    }
  }, [user, fetchProfile, location]);

  return (
    <ProfileContext.Provider
      value={{
        profile,
        setProfile,
        fetchProfile, // Keep for manual refresh if needed
        profileLoading,
        profileError,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
};

export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (context === undefined) {
    throw new Error("useProfile must be used within a ProfileProvider");
  }
  return context;
};
